import { useState, useCallback } from 'react';
import bookingService from '../../services/bookingService';

const usePjoRejection = () => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState(null);

  const loadOptions = useCallback(async (bookingToken) => {
    if (!bookingToken) return null;

    setLoading(true);
    setError(null);
    setExpired(false);

    try {
      const response = await bookingService.getPjoRejectionOptions(bookingToken);
      if (response.success && response.data) {
        setOptions(response.data);
        return response.data;
      }
      setError(response.message || 'Could not load your options.');
      return null;
    } catch (err) {
      if (err.status === 410) {
        setExpired(true);
        setError(
          err.message ||
            'The decision deadline has passed. Your booking may have been automatically refunded.'
        );
      } else {
        setError(err.message || 'Could not load your options. Please try again.');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitChoice = useCallback(async (bookingToken, choice) => {
    if (!bookingToken || !choice) {
      return { success: false, message: 'Invalid request.' };
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await bookingService.submitPjoRejectionChoice(bookingToken, choice);
      if (response.success) {
        return { success: true, data: response.data, message: response.message };
      }
      const message = response.message || 'Could not submit your choice.';
      setError(message);
      return { success: false, message };
    } catch (err) {
      if (err.status === 410) {
        setExpired(true);
        const message =
          err.message ||
          'The decision deadline has passed. Your booking may have been automatically refunded.';
        setError(message);
        return { success: false, expired: true, message };
      }
      const message = err.message || 'Could not submit your choice. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setOptions(null);
    setLoading(false);
    setSubmitting(false);
    setExpired(false);
    setError(null);
  }, []);

  return {
    options,
    loading,
    submitting,
    expired,
    error,
    loadOptions,
    submitChoice,
    reset,
  };
};

export default usePjoRejection;
